import { Directive, HostBinding, Injectable, Input, OnDestroy, Optional, Provider, SkipSelf } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AMP_TRACK_CONTEXT_DEFAULT, resolveAmpTrackContextFromUrl } from './amp-track-context-registry';
import type {
  AmpTrackComponentValue,
  AmpTrackContextValue,
  AmpTrackDetailValue
} from './generated/amp-track-types.generated';

export type {
  AmpTrackComponentValue,
  AmpTrackComponentDetailMap,
  AmpTrackContextValue,
  AmpTrackDetailForComponent,
  AmpTrackDetailValue
} from './generated/amp-track-types.generated';

/** DI token for resolving page-level `data-amp-track-context`. */
@Injectable()
export abstract class AmpTrackCtx {
  abstract get value(): AmpTrackContextValue;
}

@Injectable()
export class StaticAmpTrackCtx extends AmpTrackCtx {
  constructor(private readonly context: AmpTrackContextValue) {
    super();
  }

  override get value(): AmpTrackContextValue {
    return this.context;
  }
}

export function provideAmpTrackCtx(context: AmpTrackContextValue): Provider {
  return { provide: AmpTrackCtx, useFactory: () => new StaticAmpTrackCtx(context) };
}

/**
 * `AmpTrackCtx` whose `value` is resolved at read-time from the current
 * `Router.url` via the canonical context registry. Use this on long-lived
 * shells (e.g. the app header) so every `ampTrack` element below it reports
 * the page the user is currently on instead of a static value.
 */
@Injectable()
export class RouterAmpTrackCtx extends AmpTrackCtx implements OnDestroy {
  private currentUrl: string;
  private readonly sub: Subscription;

  constructor(private readonly router: Router) {
    super();
    this.currentUrl = router.url;
    this.sub = router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.currentUrl = e.urlAfterRedirects || e.url;
      });
  }

  override get value(): AmpTrackContextValue {
    return resolveAmpTrackContextFromUrl(this.currentUrl);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}

export function provideAmpTrackCtxFromUrl(): Provider {
  return { provide: AmpTrackCtx, useClass: RouterAmpTrackCtx };
}

@Injectable()
@Directive({
  selector: '[ampTrackCtx]',
  providers: [{ provide: AmpTrackCtx, useExisting: AmpTrackCtxDirective }],
})
export class AmpTrackCtxDirective extends AmpTrackCtx {
  @Input() ampTrackCtx!: AmpTrackContextValue;

  override get value(): AmpTrackContextValue {
    return this.ampTrackCtx;
  }

  @HostBinding('attr.data-amp-track-context') get contextAttr(): AmpTrackContextValue {
    return this.ampTrackCtx;
  }
}

@Directive({ selector: '[ampTrack]' })
export class AmpTrackDirective {
  @Input() ampTrack: AmpTrackComponentValue | null = null;
  @Input() ampTrackDetail: AmpTrackDetailValue | null = null;
  @Input() ampTrackContext: AmpTrackContextValue | null = null;

  @HostBinding('attr.data-amp-track-component') get component() { return this.ampTrack || null; }
  @HostBinding('attr.data-amp-track-detail') get detail() { return this.ampTrackDetail || null; }
  @HostBinding('attr.data-amp-track-context') get context() { return this.ampTrackContext ?? this.ctx?.value ?? null; }

  constructor(@Optional() @SkipSelf() private ctx: AmpTrackCtx) {}
}
